import React from 'react'
import { css } from '@emotion/css'
import { useIntl, FormatDateOptions } from 'react-intl'
import { selectUnit } from '@formatjs/intl-utils'
import {
  theme,
  ThemeFonts
} from '@contrast-security-inc/design-system-foundations'
import { InformationalPill } from '@contrast-security-inc/design-system-badges'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { CodeBlock } from '@contrast-security-inc/design-system-surfaces'
import { Highlight } from './add-agent/download-agent/wizard/common/highlight'
import { Info } from './add-agent/download-agent/wizard/common/info'
import { Warning } from './add-agent/download-agent/wizard/common/warning'
import { Text, Link } from '@contrast-security-inc/design-system-typography'
import { Table } from '@contrast-security-inc/design-system-tables'

export function selectRelativeUnits(date: Date | string) {
  return date instanceof Date ? selectUnit(date) : selectUnit(new Date(date))
}

const DocLink = ({ path = '', children }) => {
  const intl = useIntl()
  const ext = intl.locale === 'ja' ? 'jp' : 'com'
  const url =
    path === ''
      ? `https://docs.contrastsecurity.${ext}/?lang=${intl.locale}` // handle inconsistent landing pages for japanese
      : `https://docs.contrastsecurity.${ext}/${intl.locale}/`

  return (
    <Link isExternal={true} href={`${url}${path}`}>
      {children}
    </Link>
  )
}

// provides all skeletor fonts as inline tags
// ie: <text-titleH3>Inline title</text-titleH3>
// used to render rich static texts for agent wizards
// only use these if they simplify things
// not as a general replacement for Text
const textFonts = Object.keys(theme.font).reduce(
  (acc, font) => ({
    ...acc,
    [`text-${font}`]: (msg) => <Text font={font as ThemeFonts}>{msg}</Text>
  }),
  {}
)

// temp until the table support hidePagination prop
const hidePaginationCss = css`
  & .pagination {
    display: none;
  }
`

export const formatStyles = {
  anchor: { a: (msg) => <a>{msg}</a> }, // do not use this, use link instead
  bold: { b: (msg) => <b>{msg}</b> },
  button: (buttonProps = {}) => ({
    button: (msg) => <Button {...buttonProps}>{msg}</Button>
  }),
  break: { br: () => <br /> },
  codeblock: {
    cb: (msg) => <CodeBlock style={{ width: '100%' }} message={msg} />
  },
  codeblockwizard: {
    cbws: (msg) => (
      <CodeBlock style={{ width: '100%', maxHeight: 'none' }} message={msg} />
    )
  },
  doclink: (linkProps = {}) => ({
    doclink: (msg) => <DocLink {...linkProps}>{msg}</DocLink>
  }),
  highlight: { hl: (msg) => <Highlight>{msg}</Highlight> },
  info: { info: (msg) => <Info>{msg}</Info> },
  ipill: (pillProps = {}) => ({
    ipill: (msg) => <InformationalPill {...pillProps} text={msg} />
  }),
  italic: { i: (msg) => <i>{msg}</i> },
  link: (linkProps = {}) => ({
    a: (msg) => <Link {...linkProps}>{msg}</Link>
  }),
  table: (tableProps) => ({
    table: () => (
      <span className={hidePaginationCss}>
        <Table {...tableProps} />
      </span>
    )
  }),
  textFonts,
  warning: { warn: (msg) => <Warning>{msg}</Warning> }
}

export const dateFormats: Record<string, FormatDateOptions> = {
  // YYYY/MM/DD hh:mm:ss AM/PM
  DATETIME_LONG: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true
  }
}
